import { useState, useEffect, useRef } from 'react'
import { get, patch, post } from '../lib/api'

const ICONS = { pdr: '📋', meeting: '📅', review: '⭐', job: '🧩', expense: '🧾', info: '🔔' }

export function getNotifSettings() {
  return {
    enabled: localStorage.getItem('eh_notif_enabled') !== '0',
    sound: localStorage.getItem('eh_notif_sound') !== '0',
  }
}

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.connect(g); g.connect(ctx.destination)
    o.type = 'sine'; o.frequency.value = 880
    g.gain.setValueAtTime(0.0001, ctx.currentTime)
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01)
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3)
    o.start(); o.stop(ctx.currentTime + 0.32)
    setTimeout(() => ctx.close(), 600)
  } catch { /* audio blocked until user interacts */ }
}

function timeAgo(iso) {
  if (!iso) return ''
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z')
  const s = Math.floor((Date.now() - d.getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

export default function NotificationBell({ t, onNavigate }) {
  const [items, setItems] = useState([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [settings, setSettings] = useState(getNotifSettings())
  const ref = useRef()
  const prevUnread = useRef(0)
  const firstLoad = useRef(true)

  async function load() {
    try {
      const r = await get('/notifications')
      const u = r.unread || 0
      setItems(r.notifications || [])
      setUnread(u)
      const s = getNotifSettings()
      // Don't alert for notifications that already existed at login — only new ones
      if (!firstLoad.current && s.enabled && s.sound && u > prevUnread.current) beep()
      prevUnread.current = u
      firstLoad.current = false
    } catch { /* ignore */ }
  }

  useEffect(() => {
    load()
    const iv = setInterval(load, 60000)
    function onSettings() { setSettings(getNotifSettings()) }
    window.addEventListener('eh-notif-settings', onSettings)
    return () => { clearInterval(iv); window.removeEventListener('eh-notif-settings', onSettings) }
  }, [])

  const showUnread = settings.enabled ? unread : 0

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  async function openItem(n) {
    if (!n.read) {
      try { await patch(`/notifications/${n.id}/read`) } catch { /* ignore */ }
      setUnread((u) => Math.max(0, u - 1))
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: 1 } : x)))
    }
    if (n.link && onNavigate) { onNavigate(n.link); setOpen(false) }
  }

  async function markAll() {
    try { await post('/notifications/read-all') } catch { /* ignore */ }
    setUnread(0)
    setItems((prev) => prev.map((x) => ({ ...x, read: 1 })))
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load() }}
        title="Notifications"
        style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 6, borderRadius: 8 }}
      >
        {settings.enabled ? '🔔' : '🔕'}
        {showUnread > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 17, height: 17, padding: '0 4px', background: '#EF4444', color: '#fff', borderRadius: 99, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {showUnread > 9 ? '9+' : showUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="eh-notif-dropdown" style={{ position: 'absolute', right: 0, top: 40, width: 340, maxHeight: 440, overflowY: 'auto', background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 200 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${t.border}`, position: 'sticky', top: 0, background: t.card }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: t.text }}>Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} style={{ background: 'none', border: 'none', color: '#4F46E5', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Mark all read</button>
            )}
            {!settings.enabled && <span style={{ fontSize: 11, color: t.textMuted }}>muted</span>}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: t.textMuted, fontSize: 13 }}>No notifications yet</div>
          ) : (
            items.map((n) => (
              <div key={n.id} onClick={() => openItem(n)}
                style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${t.border}`, cursor: 'pointer', background: n.read ? 'transparent' : (t.dark ? 'rgba(99,102,241,0.12)' : '#F5F7FF') }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>{ICONS[n.type] || '🔔'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{n.title}</div>
                  {n.body && <div style={{ fontSize: 12, color: t.textSub, marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>}
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4F46E5', flexShrink: 0, marginTop: 5 }} />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
